import type { FeedAttachment, FeedPost } from "@/lib/feed/types";
import { parseLinkPreview, type LinkPreview } from "@/lib/feed/link-preview";

export const FEED_AD_BUCKET = "feed-ad-attachments";
export const MAX_FEED_AD_FILES = 4;
export const DEFAULT_FEED_AD_SLOT_EVERY = 4;
export const FEED_AD_SLOT_EVERY_OPTIONS = [3, 4, 5, 6, 8] as const;

export type FeedAdPlacement = "home" | "community" | "both";

export type FeedAd = {
  id: string;
  content: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  weight: number;
  linkPreview: LinkPreview | null;
  attachments: FeedAttachment[];
};

export type FeedAdsPayload = {
  ads: FeedAd[];
  slotEvery: number;
};

export type AdminFeedAdRow = {
  id: string;
  title: string;
  contentPreview: string | null;
  isActive: boolean;
  placement: FeedAdPlacement;
  weight: number;
  startsAt: string | null;
  endsAt: string | null;
  communityNames: string | null;
  attachmentCount: number;
  createdAt: string;
};

export type AdminFeedAdDetail = {
  id: string;
  title: string;
  content: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  weight: number;
  placement: FeedAdPlacement;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
  communityIds: string[];
  linkPreview: LinkPreview | null;
  attachments: FeedAttachment[];
};

export type FeedAdSettings = {
  slotEvery: number;
};

export type FeedAdAttachmentInput = {
  attachmentType: FeedAttachment["type"];
  storageBucket: string;
  storagePath: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
};

export type UpsertFeedAdInput = {
  id?: string;
  title: string;
  content: string | null;
  ctaLabel: string | null;
  ctaUrl: string | null;
  weight: number;
  placement: FeedAdPlacement;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
  communityIds: string[];
  attachments: FeedAdAttachmentInput[];
  linkPreview: LinkPreview | null;
};

export const FEED_AD_PLACEMENTS: FeedAdPlacement[] = [
  "home",
  "community",
  "both",
];

export const FEED_AD_PLACEMENT_LABELS: Record<FeedAdPlacement, string> = {
  home: "Fő hírfolyam",
  community: "Közösségi hírfolyam",
  both: "Mindkettő",
};

export function feedAdWeightLabel(weight: number) {
  if (weight <= 3) {
    return "Ritka";
  }
  if (weight <= 7) {
    return "Normál";
  }
  return "Gyakori";
}

export function clampFeedAdSlotEvery(value: number) {
  if (!Number.isFinite(value)) {
    return DEFAULT_FEED_AD_SLOT_EVERY;
  }
  return Math.min(8, Math.max(3, Math.round(value)));
}

export type FeedTimelineItem =
  | { kind: "post"; key: string; post: FeedPost }
  | { kind: "ad"; key: string; ad: FeedAd };

export function asFeedAdPlacement(value: string): FeedAdPlacement {
  if (value === "community" || value === "both") {
    return value;
  }
  return "home";
}

export function linkPreviewFromMetadata(metadata: unknown): LinkPreview | null {
  if (!metadata || typeof metadata !== "object") {
    return null;
  }
  const payload = metadata as { link?: unknown };
  return parseLinkPreview(payload.link);
}

function hashSeed(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed: number) {
  return () => {
    let next = (seed += 0x6d2b79f5);
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

function pickWeighted(ads: FeedAd[], random: () => number) {
  const total = ads.reduce((sum, ad) => sum + Math.max(1, ad.weight), 0);
  let cursor = random() * total;
  for (const ad of ads) {
    cursor -= Math.max(1, ad.weight);
    if (cursor <= 0) {
      return ad;
    }
  }
  return ads[ads.length - 1]!;
}

export function mixFeedAds(
  posts: FeedPost[],
  ads: FeedAd[],
  seedKey: string,
  slotEvery = DEFAULT_FEED_AD_SLOT_EVERY,
): FeedTimelineItem[] {
  if (posts.length === 0) {
    return [];
  }

  if (ads.length === 0) {
    return posts.map((post) => ({
      kind: "post" as const,
      key: post.id,
      post,
    }));
  }

  const gap = clampFeedAdSlotEvery(slotEvery);
  const day = new Date().toISOString().slice(0, 10);
  const mixed: FeedTimelineItem[] = [];
  let lastAdId: string | null = null;

  posts.forEach((post, index) => {
    mixed.push({ kind: "post", key: post.id, post });
    const position = index + 1;
    if (position % gap !== 0) {
      return;
    }

    const pool =
      lastAdId && ads.length > 1
        ? ads.filter((ad) => ad.id !== lastAdId)
        : ads;
    const random = mulberry32(hashSeed(`${seedKey}:${day}:${position}`));
    const picked = pickWeighted(pool, random);
    lastAdId = picked.id;
    mixed.push({
      kind: "ad",
      key: `ad-${picked.id}-${position}`,
      ad: picked,
    });
  });

  return mixed;
}
